import { useEffect, useId, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import type { DemoAccount, Enrolment, EnrolmentSetup, SignInOptions } from '@adiox/api';
import { Wordmark } from '../shell/Brand';
import { loginErrorMessage, secondStepErrorMessage } from './loginError';
import { QrCode } from './QrCode';
import { useT } from '../i18n/i18n';
import { LanguagePicker } from '../i18n/LanguagePicker';
import { useSessionContext } from './session';

type Step =
  | { kind: 'credentials' }
  | { kind: 'demo' }
  | { kind: 'demo-done'; account: DemoAccount }
  | { kind: 'verify'; token: string }
  | { kind: 'enrol'; token: string; setup: EnrolmentSetup }
  | { kind: 'codes'; enrolment: Enrolment }
  | { kind: 'forgot'; sent: boolean }
  | { kind: 'reset'; token: string };

/**
 * A "forgot password" link opens the page with ?reset=<token>. The token is taken out of the address at once, so it does not
 * stay in the browser history or reach another page.
 */
function takeResetToken(): string | null {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('reset');
    if (!token) return null;
    url.searchParams.delete('reset');
    window.history.replaceState(window.history.state, '', url.toString());
    return token;
  } catch {
    return null;
  }
}

const inputClass =
  'mb-4 w-full rounded-md border border-line bg-panel2 px-3 py-2 outline-none focus:border-accent';
const buttonClass =
  'w-full rounded-md bg-accent px-4 py-2.5 font-semibold text-accent-fg transition hover:brightness-110 disabled:cursor-wait disabled:opacity-60';
const linkButtonClass =
  'mt-3 w-full text-center text-sm text-muted underline-offset-2 hover:text-fg hover:underline';

function Card({
  title,
  lead,
  notice,
  onSubmit,
  children,
}: {
  title: string;
  lead: string;
  notice?: string;
  onSubmit: (event: FormEvent) => void;
  children: ReactNode;
}) {
  return (
    <main className="grid min-h-full place-items-center bg-bg p-4">
      <form
        onSubmit={onSubmit}
        aria-labelledby="login-title"
        className="w-full max-w-sm rounded-[var(--radius)] border border-line bg-panel p-8 shadow-2xl"
      >
        <Wordmark className="mb-6 h-9" />
        <h1 id="login-title" className="mb-1 text-xl font-semibold">
          {title}
        </h1>
        <p className="mb-6 text-sm text-muted">{lead}</p>
        {notice && (
          <p role="status" className="mb-4 rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-warn">
            {notice}
          </p>
        )}
        {children}
      </form>
    </main>
  );
}

/** The account number is remembered on this device only (never in the synced preferences: nobody is signed in yet). */
const REMEMBER_KEY = 'adiox.rememberedAccount';
function readRememberedAccount(): string {
  try {
    return localStorage.getItem(REMEMBER_KEY) ?? '';
  } catch {
    return '';
  }
}
function rememberAccount(value: string | null): void {
  try {
    if (value) localStorage.setItem(REMEMBER_KEY, value);
    else localStorage.removeItem(REMEMBER_KEY);
  } catch {
    // private mode: nothing is remembered, which is fine
  }
}

/** A password field with a show/hide button and a Caps Lock warning (a wrong-case password is the commonest failure). */
function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
}) {
  const [shown, setShown] = useState(false);
  const [caps, setCaps] = useState(false);
  const check = (e: KeyboardEvent<HTMLInputElement>) => setCaps(e.getModifierState?.('CapsLock') ?? false);
  return (
    <div className="mb-4">
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          autoComplete={autoComplete}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={check}
          onKeyUp={check}
          onBlur={() => setCaps(false)}
          aria-describedby={caps ? `${id}-caps` : undefined}
          className={`${inputClass.replace('mb-4 ', '')} pr-16`}
        />
        <button
          type="button"
          aria-pressed={shown}
          aria-label={shown ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          onClick={() => setShown(!shown)}
          className="absolute right-1 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs text-muted hover:text-fg"
        >
          {shown ? 'Hide' : 'Show'}
        </button>
      </div>
      {caps && (
        <p id={`${id}-caps`} role="status" className="mt-1 text-xs text-warn">
          Caps Lock is on.
        </p>
      )}
    </div>
  );
}

function ErrorLine({ error }: { error: string | null }) {
  return (
    <p role="alert" aria-live="assertive" className="mb-4 min-h-5 text-sm text-down">
      {error}
    </p>
  );
}

export function LoginScreen({
  notice,
  identifier = 'email',
}: {
  notice?: string;
  /** What the first field asks for: an email (staff, the manager portal) or a trading account number (the terminal, MT5-style). */
  identifier?: 'email' | 'account';
}) {
  const { tokens } = useSessionContext();
  const t = useT();
  const emailId = useId();
  const passwordId = useId();
  const codeId = useId();
  const [step, setStep] = useState<Step>(() => {
    const token = takeResetToken();
    return token ? { kind: 'reset', token } : { kind: 'credentials' };
  });
  const [localNotice, setLocalNotice] = useState<string | null>(null);
  // A remembered account number is filled in for the user (terminal only).
  const [email, setEmail] = useState(() => (identifier === 'account' ? readRememberedAccount() : ''));
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const repeatId = useId();
  const rememberId = useId();
  const [remember, setRemember] = useState(() => identifier === 'account' && readRememberedAccount() !== '');
  const [forgot, setForgot] = useState(false);
  const [investor, setInvestor] = useState(false); // sign in with the read-only (investor) password
  const [repeat, setRepeat] = useState('');
  const [options, setOptions] = useState<SignInOptions | null>(null);

  // Whether the server offers demo sign-up, and how to reach support.
  useEffect(() => {
    let cancelled = false;
    void tokens.signInOptions?.().then((o) => !cancelled && o && setOptions(o));
    return () => {
      cancelled = true;
    };
  }, [tokens, identifier]);

  const startOver = () => {
    setStep({ kind: 'credentials' });
    setCode('');
    setRecovery(false);
    setError(null);
    setBusy(false);
  };

  /** Runs one server call with the shared busy/error handling. */
  const run = async (action: () => Promise<void>, explain: (e: unknown) => string, onFail?: () => void) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(explain(e));
      onFail?.();
    }
    setBusy(false);
  };

  const submitCredentials = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (identifier === 'account' && !/^[1-9][0-9]*$/.test(trimmed)) {
      setError('Enter your account number (digits only).');
      return;
    }

    void run(
      async () => {
        if (identifier === 'account' && investor) {
          await tokens.loginInvestor(Number(trimmed), password);
          return;
        }
        const outcome =
          identifier === 'account'
            ? await tokens.loginByAccount(Number(trimmed), password)
            : await tokens.login(trimmed, password);
        if (identifier === 'account') rememberAccount(remember ? trimmed : null);
        if (outcome.status === 'signed-in') return;
        setPassword(''); // the password is not needed again, and never stays in the form
        setCode('');
        if (outcome.kind === 'verify') {
          setStep({ kind: 'verify', token: outcome.challengeToken });
        } else {
          setStep({
            kind: 'enrol',
            token: outcome.challengeToken,
            setup: await tokens.startEnrolment(outcome.challengeToken),
          });
        }
      },
      (e) => loginErrorMessage(e, identifier),
      () => setPassword(''), // never leave a rejected password sitting in the form
    );
  };

  const submitDemo = (event: FormEvent) => {
    event.preventDefault();
    const address = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) return setError('Enter your email address.');
    if (password.length < 12) return setError('Choose a password of at least 12 characters.');
    if (password !== repeat) return setError('The two passwords are not the same.');
    void run(
      async () => {
        const account = await tokens.registerDemo(address, password);
        setPassword('');
        setRepeat('');
        setStep({ kind: 'demo-done', account });
      },
      (e) => loginErrorMessage(e),
    );
  };

  const openDemo = () => {
    setStep({ kind: 'demo' });
    setEmail('');
    setPassword('');
    setRepeat('');
    setError(null);
  };

  const submitVerify = (event: FormEvent) => {
    event.preventDefault();
    if (step.kind !== 'verify') return;
    void run(
      () => tokens.verifyTwoFactor(step.token, code.trim()),
      secondStepErrorMessage,
      () => setCode(''),
    );
  };

  const submitEnrol = (event: FormEvent) => {
    event.preventDefault();
    if (step.kind !== 'enrol') return;
    void run(
      async () =>
        setStep({ kind: 'codes', enrolment: await tokens.confirmEnrolment(step.token, code.trim()) }),
      secondStepErrorMessage,
      () => setCode(''),
    );
  };

  if (step.kind === 'forgot') {
    const submitForgot = (event: FormEvent) => {
      event.preventDefault();
      const value = email.trim();
      if (identifier === 'account' && !/^[1-9][0-9]*$/.test(value))
        return setError('Enter your account number (digits only).');
      if (identifier === 'email' && !/^[^\s@]+@[^\s@]+$/.test(value))
        return setError('Enter your email address.');
      void run(
        async () => {
          await tokens.forgotPassword(identifier === 'account' ? { login: Number(value) } : { email: value });
          setStep({ kind: 'forgot', sent: true });
        },
        (e) => loginErrorMessage(e, identifier),
      );
    };
    return (
      <Card
        title={t('Forgot your password?')}
        lead={
          step.sent
            ? 'Check your email.'
            : `Enter your ${identifier === 'account' ? 'account number' : 'email address'} and we will email you a link to choose a new password.`
        }
        onSubmit={submitForgot}
      >
        {step.sent ? (
          <p role="status" className="mb-4 rounded-md border border-line bg-panel2 p-3 text-sm">
            If that account exists, an email with a link is on its way to the address on file. The link works
            once, for 30 minutes. Nothing arrived? Check the spam folder, or contact support.
          </p>
        ) : (
          <>
            <label htmlFor={emailId} className="mb-1 block text-sm font-medium">
              {identifier === 'account' ? t('Account number') : t('Email')}
            </label>
            <input
              id={emailId}
              type={identifier === 'account' ? 'text' : 'email'}
              inputMode={identifier === 'account' ? 'numeric' : undefined}
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
            <ErrorLine error={error} />
            <button type="submit" disabled={busy} className={buttonClass}>
              {busy ? 'Sending…' : t('Email me a link')}
            </button>
          </>
        )}
        <button type="button" className={linkButtonClass} onClick={startOver}>
          {t('Back to sign in')}
        </button>
      </Card>
    );
  }

  if (step.kind === 'reset') {
    const submitReset = (event: FormEvent) => {
      event.preventDefault();
      if (password.length < 12) return setError('Choose a password of at least 12 characters.');
      if (password !== repeat) return setError('The two passwords are not the same.');
      void run(
        async () => {
          await tokens.resetPassword(step.token, password);
          setPassword('');
          setRepeat('');
          setLocalNotice('Your password was changed. Sign in with the new one.');
          setStep({ kind: 'credentials' });
        },
        (e) => loginErrorMessage(e, identifier),
      );
    };
    return (
      <Card
        title={t('Choose a new password')}
        lead="You will then sign in with it; every other session is signed out."
        onSubmit={submitReset}
      >
        <PasswordField
          id={passwordId}
          label={t('New password')}
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
        />
        <PasswordField
          id={repeatId}
          label={t('Repeat the new password')}
          value={repeat}
          onChange={setRepeat}
          autoComplete="new-password"
        />
        <p className="-mt-2 mb-3 text-xs text-muted">At least 12 characters.</p>
        <ErrorLine error={error} />
        <button type="submit" disabled={busy} className={buttonClass}>
          {busy ? 'Saving…' : t('Set the new password')}
        </button>
        <button type="button" className={linkButtonClass} onClick={startOver}>
          {t('Back to sign in')}
        </button>
      </Card>
    );
  }

  if (step.kind === 'demo') {
    return (
      <Card
        title={t('Open a demo account')}
        lead={`Practise with virtual money${
          options?.demoDeposit ? ` (${options.demoDeposit.toLocaleString('en-US')} to start with)` : ''
        }. Nothing is real, and nothing is charged.`}
        onSubmit={submitDemo}
      >
        <label htmlFor={emailId} className="mb-1 block text-sm font-medium">
          Email
        </label>
        <input
          id={emailId}
          type="email"
          autoComplete="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
        />
        <PasswordField
          id={passwordId}
          label="Password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
        />
        <PasswordField
          id={repeatId}
          label="Repeat the password"
          value={repeat}
          onChange={setRepeat}
          autoComplete="new-password"
        />
        <p className="-mt-2 mb-3 text-xs text-muted">At least 12 characters.</p>
        <ErrorLine error={error} />
        <button type="submit" disabled={busy} className={buttonClass}>
          {busy ? 'Opening…' : 'Open the demo account'}
        </button>
        <button type="button" className={linkButtonClass} onClick={startOver}>
          {t('Back to sign in')}
        </button>
      </Card>
    );
  }

  if (step.kind === 'demo-done') {
    const number = String(step.account.login);
    return (
      <Card
        title="Your demo account is ready"
        lead="You sign in with this account number and the password you chose. Write the number down."
        onSubmit={(event) => {
          event.preventDefault();
          rememberAccount(remember ? number : null);
          step.account.finish();
        }}
      >
        <p
          className="num mb-4 rounded-md border border-accent bg-panel2 p-4 text-center text-3xl font-semibold tracking-wider"
          data-testid="demo-account-number"
        >
          {number}
        </p>
        <label className="mb-4 flex items-center gap-2 text-sm" htmlFor={rememberId}>
          <input
            id={rememberId}
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          {t('Remember my account number on this device')}
        </label>
        <button type="submit" autoFocus className={buttonClass}>
          Continue to the terminal
        </button>
      </Card>
    );
  }

  if (step.kind === 'verify') {
    return (
      <Card
        title="Two-step verification"
        lead={
          recovery
            ? 'Enter one of your recovery codes. Each works once.'
            : 'Enter the 6-digit code from your authenticator app.'
        }
        onSubmit={submitVerify}
      >
        <label htmlFor={codeId} className="mb-1 block text-sm font-medium">
          {recovery ? 'Recovery code' : 'Authenticator code'}
        </label>
        <input
          id={codeId}
          key={recovery ? 'recovery' : 'totp'}
          type="text"
          inputMode={recovery ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          autoCapitalize="characters"
          spellCheck={false}
          required
          autoFocus
          maxLength={recovery ? 12 : 6}
          pattern={recovery ? undefined : '[0-9]{6}'}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className={`${inputClass} num tracking-widest`}
        />
        <ErrorLine error={error} />
        <button type="submit" disabled={busy} className={buttonClass}>
          {busy ? 'Checking…' : 'Verify'}
        </button>
        <button
          type="button"
          className={linkButtonClass}
          onClick={() => {
            setRecovery(!recovery);
            setCode('');
            setError(null);
          }}
        >
          {recovery ? 'Use the authenticator app instead' : 'Use a recovery code instead'}
        </button>
        <button type="button" className={linkButtonClass} onClick={startOver}>
          Start again
        </button>
      </Card>
    );
  }

  if (step.kind === 'enrol') {
    return (
      <Card
        title="Set up two-step verification"
        lead="Managers and Admins sign in with a code from an authenticator app. Scan this with your app (Google Authenticator, Microsoft Authenticator, 1Password, Authy…)."
        onSubmit={submitEnrol}
      >
        <div className="mb-3 flex justify-center">
          <QrCode value={step.setup.uri} label="QR code for your authenticator app" />
        </div>
        <p className="mb-1 text-xs text-muted">Cannot scan? Type this key into the app instead:</p>
        <p
          className="num mb-4 break-all rounded-md border border-line bg-panel2 p-2 text-sm select-all"
          data-testid="totp-secret"
        >
          {step.setup.secret.replace(/(.{4})/g, '$1 ').trim()}
        </p>
        <label htmlFor={codeId} className="mb-1 block text-sm font-medium">
          Code shown in the app
        </label>
        <input
          id={codeId}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          autoFocus
          maxLength={6}
          pattern="[0-9]{6}"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className={`${inputClass} num tracking-widest`}
        />
        <ErrorLine error={error} />
        <button type="submit" disabled={busy} className={buttonClass}>
          {busy ? 'Checking…' : 'Turn on and continue'}
        </button>
        <button type="button" className={linkButtonClass} onClick={startOver}>
          Cancel
        </button>
      </Card>
    );
  }

  if (step.kind === 'codes') {
    const text = step.enrolment.recoveryCodes.join('\n');
    return (
      <Card
        title="Save your recovery codes"
        lead="If you lose your phone, each of these signs you in once. They are shown only now: store them somewhere safe, apart from your password."
        onSubmit={(event) => {
          event.preventDefault();
          if (saved) step.enrolment.finish();
        }}
      >
        <ul
          className="num mb-3 grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-line bg-panel2 p-3 text-sm"
          data-testid="recovery-codes"
        >
          {step.enrolment.recoveryCodes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <button
          type="button"
          className="mb-4 w-full rounded-md border border-line px-3 py-2 text-sm hover:border-accent"
          onClick={() => {
            void navigator.clipboard?.writeText(text).then(
              () => setCopied(true),
              () => setCopied(false),
            );
          }}
        >
          {copied ? 'Copied' : 'Copy the codes'}
        </button>
        <label className="mb-4 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={saved}
            onChange={(e) => setSaved(e.target.checked)}
            className="mt-1"
          />
          I have saved these recovery codes
        </label>
        <button type="submit" disabled={!saved} className={buttonClass}>
          Continue
        </button>
      </Card>
    );
  }

  return (
    <Card
      title={investor ? t('Investor sign-in') : t('Sign in')}
      lead={
        investor
          ? t('View this account with its investor password. You can look, but not trade or change anything.')
          : t('Trade, chart and manage your account.')
      }
      notice={notice ?? localNotice ?? undefined}
      onSubmit={submitCredentials}
    >
      <label htmlFor={emailId} className="mb-1 block text-sm font-medium">
        {identifier === 'account' ? t('Account number') : t('Email')}
      </label>
      <input
        id={emailId}
        type={identifier === 'account' ? 'text' : 'email'}
        inputMode={identifier === 'account' ? 'numeric' : undefined}
        autoComplete="username"
        required
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={`${inputClass}${identifier === 'account' ? ' num' : ''}`}
      />

      <PasswordField
        id={passwordId}
        label={investor ? t('Investor password') : t('Password')}
        value={password}
        onChange={setPassword}
        autoComplete="current-password"
      />

      {identifier === 'account' && (
        <label className="-mt-2 mb-3 flex items-center gap-2 text-sm text-muted" htmlFor={rememberId}>
          <input
            id={rememberId}
            type="checkbox"
            checked={remember}
            onChange={(e) => {
              setRemember(e.target.checked);
              if (!e.target.checked) rememberAccount(null); // forget it now, not only at the next sign-in
            }}
          />
          {t('Remember my account number on this device')}
        </label>
      )}

      <ErrorLine error={error} />

      <button type="submit" disabled={busy} className={buttonClass}>
        {busy ? t('Signing in…') : investor ? t('View the account') : t('Sign in')}
      </button>

      {identifier === 'account' && (
        <button
          type="button"
          className={linkButtonClass}
          onClick={() => {
            setInvestor(!investor);
            setPassword('');
            setError(null);
          }}
        >
          {investor ? t('Use my own password instead') : t('Use the investor (read-only) password')}
        </button>
      )}

      <button
        type="button"
        className={linkButtonClass}
        aria-expanded={forgot}
        aria-controls="forgot-password"
        onClick={() => {
          if (options?.passwordReset) {
            setError(null);
            setStep({ kind: 'forgot', sent: false });
          } else setForgot(!forgot);
        }}
      >
        {t('Forgot your password?')}
      </button>
      {forgot && (
        <p id="forgot-password" className="mt-2 rounded-md border border-line bg-panel2 p-3 text-sm">
          {identifier === 'email' ? (
            'Ask an Admin to set a new password for you. For your safety it is never sent by email.'
          ) : options?.supportContact ? (
            <>
              Contact support to have a new password set: <SupportLink contact={options.supportContact} />.
              Have your account number ready.
            </>
          ) : (
            "Contact your broker's support team to have a new password set. Have your account number ready."
          )}
        </p>
      )}

      <LanguagePicker className="mt-4 justify-center" />
      {options?.demoRegistration && (
        <p className="mt-4 border-t border-line pt-4 text-center text-sm text-muted">
          {t('New here?')}{' '}
          <button
            type="button"
            className="font-semibold text-accent underline-offset-2 hover:underline"
            onClick={openDemo}
          >
            {t('Open a demo account')}
          </button>
        </p>
      )}
    </Card>
  );
}

/** An email address becomes a mail link, a web address a link; anything else is shown as it is. */
function SupportLink({ contact }: { contact: string }) {
  if (/^[^\s@]+@[^\s@]+$/.test(contact)) {
    return (
      <a className="text-accent underline" href={`mailto:${contact}`}>
        {contact}
      </a>
    );
  }
  if (/^https?:\/\//.test(contact)) {
    return (
      <a className="text-accent underline" href={contact} target="_blank" rel="noreferrer">
        {contact}
      </a>
    );
  }
  return <span className="font-semibold">{contact}</span>;
}
